import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Plus, Trophy, X, ArrowLeft, Users, Minus, Coins, Star, NavigationArrow, Lock, Sparkle, Check } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

export default function Leagues() {
  const [leagues, setLeagues] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const { user } = useAuth();
  const isPro =
    user?.role === "admin" ||
    ["active", "trialing"].includes(user?.subscription_status);

  const load = async () => {
    const { data } = await api.get("/leagues/mine");
    setLeagues(data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="px-5 pt-6">
      <Link to="/app" className="btn-ghost -ml-2 mb-3" data-testid="leagues-back-link"><ArrowLeft size={20} weight="bold" /></Link>

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)]">Leagues</h1>
        <div className="flex gap-3 text-xs font-bold">
          <Link to="/app/seasons" className="text-[var(--primary)] hover:underline" data-testid="leagues-seasons-link">
            Seasons →
          </Link>
          <Link to="/app/leaderboard" className="text-[var(--primary)] hover:underline" data-testid="leagues-leaderboard-link">
            Club Board →
          </Link>
        </div>
      </div>
      <p className="text-[var(--text-secondary)] mb-5">Battle your mates round after round.</p>

      {isPro ? (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button onClick={() => setShowCreate(true)} className="surface-card-flat p-4 text-left hover:shadow-md transition-shadow" data-testid="open-create-league">
            <div className="h-10 w-10 rounded-xl bg-[var(--primary)] text-white flex items-center justify-center mb-2">
              <Plus size={18} weight="bold" />
            </div>
            <div className="font-heading font-bold text-[var(--primary)]">Create</div>
            <div className="text-xs text-[var(--text-secondary)]">Start a new league</div>
          </button>
          <button onClick={() => setShowJoin(true)} className="surface-card-flat p-4 text-left hover:shadow-md transition-shadow" data-testid="open-join-league">
            <div className="h-10 w-10 rounded-xl bg-[var(--accent-terracotta)] text-white flex items-center justify-center mb-2">
              <Users size={18} weight="bold" />
            </div>
            <div className="font-heading font-bold text-[var(--primary)]">Join</div>
            <div className="text-xs text-[var(--text-secondary)]">Use invite code</div>
          </button>
        </div>
      ) : (
        <div className="surface-card p-5 mb-6" data-testid="leagues-pro-gate">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={18} weight="fill" className="text-[var(--accent-terracotta)]" />
            <h3 className="font-heading font-bold text-[var(--primary)]">Leagues are a Pro feature</h3>
          </div>
          <ul className="space-y-1.5 text-sm mb-4">
            {[
              "Create unlimited leagues with mates",
              "Money pots with PayPal / Monzo / Revolut payout handles",
              "Per-course rounds-required targets",
              "vs Par · Total strokes · Best round scoring",
            ].map((perk) => (
              <li key={perk} className="flex items-start gap-2">
                <Check size={14} weight="bold" className="text-[var(--primary)] shrink-0 mt-0.5" />
                <span className="text-[var(--text)]">{perk}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/subscribe"
            data-testid="leagues-go-pro"
            className="btn-primary w-full"
          >
            <Lock size={16} weight="bold" /> Go Pro to unlock
          </Link>
        </div>
      )}

      <h3 className="font-heading font-bold text-lg text-[var(--primary)] mb-3">Your leagues</h3>
      {leagues.length === 0 ? (
        <div className="surface-card-flat p-5 text-[var(--text-secondary)]" data-testid="no-leagues-yet">
          No leagues yet. Create one or join with a mate's code.
        </div>
      ) : (
        <div className="space-y-3" data-testid="leagues-list">
          {leagues.map((l) => (
            <Link key={l.id} to={`/app/leagues/${l.id}`} data-testid={`league-${l.id}`} className="block surface-card-flat p-4 hover:bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-[var(--primary)] text-white flex items-center justify-center">
                    <Trophy size={22} weight="fill" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-[var(--primary)]">{l.name}</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {l.member_ids.length} player{l.member_ids.length === 1 ? "" : "s"} · Code <span className="font-mono-num font-bold">{l.code}</span>
                    </div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-[var(--primary)]">→</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateLeagueDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {showJoin && <JoinLeagueDialog onClose={() => setShowJoin(false)} onJoined={() => { setShowJoin(false); load(); }} />}
    </div>
  );
}

function CreateLeagueDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ruleType, setRuleType] = useState("vs_par");
  const [stablefordNet, setStablefordNet] = useState(false);
  const [potAmount, setPotAmount] = useState("");
  const [potCurrency, setPotCurrency] = useState("GBP");
  const [payoutMethod, setPayoutMethod] = useState("");
  const [courses, setCourses] = useState([]);
  const [requirements, setRequirements] = useState([]); // [{course_id, course_name, rounds_required}]
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [courseQ, setCourseQ] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  // Quick-filter chips above the search box.
  //   "all"   — default, alphabetical
  //   "favs"  — only my starred courses
  //   "near"  — sorted by distance from my GPS (asks for permission on tap)
  const [filterMode, setFilterMode] = useState("all");
  const [gpsPos, setGpsPos] = useState(null);
  const { user } = useAuth();
  const favIds = new Set(user?.favourite_course_ids || []);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/courses/lite");
        setCourses(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const requestGps = () => {
    if (!("geolocation" in navigator)) {
      setError("This device doesn't support GPS.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setGpsPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        setFilterMode("near");
      },
      () => setError("Couldn't get your location."),
      { enableHighAccuracy: false, timeout: 5000 },
    );
  };

  const distanceMiles = (a, b) => {
    if (!a || !b || a.lat == null || a.lng == null) return Infinity;
    const R = 3958.8;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  };

  // Filtered suggestions for the search-as-you-type course picker.
  // Order: filter chip → optional text search → cap at 30.
  const filteredCourses = (() => {
    const q = courseQ.trim().toLowerCase();
    const reqIds = new Set(requirements.map((r) => r.course_id));
    let pool = courses.filter((c) => !reqIds.has(c.id));
    if (filterMode === "favs") pool = pool.filter((c) => favIds.has(c.id));
    if (filterMode === "near" && gpsPos) {
      pool = pool
        .filter((c) => c.centroid_lat != null && c.centroid_lng != null)
        .map((c) => ({ ...c, _dist: distanceMiles(gpsPos, { lat: c.centroid_lat, lng: c.centroid_lng }) }))
        .sort((a, b) => a._dist - b._dist);
    }
    if (q) {
      pool = pool.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.location || "").toLowerCase().includes(q)
      );
    }
    return pool.slice(0, 30);
  })();

  const addRequirement = (courseId) => {
    const cid = courseId || selectedCourseId;
    if (!cid) return;
    if (requirements.some((r) => r.course_id === cid)) return;
    const course = courses.find((c) => c.id === cid);
    if (!course) return;
    setRequirements([...requirements, { course_id: course.id, course_name: course.name, rounds_required: 5 }]);
    setSelectedCourseId("");
    setCourseQ("");
    setShowSuggest(false);
  };

  const setRoundsForReq = (idx, delta) => {
    setRequirements((prev) =>
      prev.map((r, i) =>
        i === idx ? { ...r, rounds_required: Math.max(1, Math.min(50, r.rounds_required + delta)) } : r
      )
    );
  };

  const removeRequirement = (idx) => {
    setRequirements((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        name,
        description: description || null,
        rule_type: ruleType,
        stableford_net: ruleType === "stableford" ? !!stablefordNet : false,
        pot_amount: potAmount ? Number(potAmount) : null,
        pot_currency: potCurrency,
        payout_method: payoutMethod || null,
        course_requirements: requirements.map((r) => ({
          course_id: r.course_id,
          rounds_required: r.rounds_required,
        })),
      };
      await api.post("/leagues", payload);
      onCreated();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const rules = [
    { id: "vs_par", label: "vs Par", desc: "Sum of (strokes − par) across all rounds. Lower wins." },
    { id: "total_strokes", label: "Total strokes", desc: "Total strokes across all rounds. Lower wins." },
    { id: "best_round", label: "Best round", desc: "Only your best round (vs par) counts. Lower wins." },
    { id: "stableford", label: "Stableford", desc: "Sum of stableford points across all rounds. Higher wins." },
  ];

  return (
    <Sheet title="Create league" onClose={onClose} testid="create-league-dialog">
      <form onSubmit={submit} className="space-y-4 p-5">
        <div>
          <label className="label-uppercase block mb-2">League name</label>
          <input className="input-field" value={name} onChange={(e)=>setName(e.target.value)} required data-testid="create-league-name" />
        </div>
        <div>
          <label className="label-uppercase block mb-2">Description (optional)</label>
          <textarea className="input-field min-h-[80px]" value={description} onChange={(e)=>setDescription(e.target.value)} data-testid="create-league-desc" />
        </div>

        {/* Pot */}
        <div>
          <label className="label-uppercase block mb-2 flex items-center gap-2">
            <Coins size={14} weight="fill" className="text-[var(--accent-terracotta)]" /> Money pot (optional)
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="1"
              className="input-field flex-1"
              placeholder="e.g. 50"
              value={potAmount}
              onChange={(e) => setPotAmount(e.target.value)}
              data-testid="create-league-pot-amount"
            />
            <select
              value={potCurrency}
              onChange={(e) => setPotCurrency(e.target.value)}
              className="input-field w-24"
              data-testid="create-league-pot-currency"
            >
              <option value="GBP">GBP £</option>
              <option value="USD">USD $</option>
              <option value="EUR">EUR €</option>
            </select>
          </div>
          <p className="text-[11px] text-[var(--text-secondary)] mt-1">
            Just a tracking number — winner takes it.
          </p>

          {/* Payout method (admin) */}
          {potAmount && (
            <div className="mt-3">
              <label className="label-uppercase block mb-2">You'll receive buy-ins / pay out via *</label>
              <div className="grid grid-cols-3 gap-2 mb-1">
                {[
                  { id: "paypal", label: "PayPal" },
                  { id: "monzo", label: "Monzo" },
                  { id: "revolut", label: "Revolut" },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPayoutMethod(payoutMethod === m.id ? "" : m.id)}
                    data-testid={`payout-method-${m.id}`}
                    className={`rounded-2xl py-2 text-xs font-heading font-bold border-2 transition-colors ${
                      payoutMethod === m.id
                        ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                        : "bg-white text-[var(--primary)] border-[var(--border-default)]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[var(--text-secondary)]">
                Members will send their buy-in to your saved handle for this app. Set yours in Profile first.
              </p>
            </div>
          )}
        </div>

        {/* Course requirements */}
        <div>
          <label className="label-uppercase block mb-2">Courses & rounds (optional)</label>

          {/* Quick filter chips */}
          <div className="flex gap-2 mb-2" data-testid="create-league-course-filters">
            <FilterChip
              active={filterMode === "all"}
              onClick={() => setFilterMode("all")}
              testid="filter-all"
            >
              All
            </FilterChip>
            <FilterChip
              active={filterMode === "favs"}
              onClick={() => setFilterMode("favs")}
              testid="filter-favs"
              icon={<Star size={11} weight={filterMode === "favs" ? "fill" : "bold"} />}
              count={favIds.size}
            >
              Favourites
            </FilterChip>
            <FilterChip
              active={filterMode === "near"}
              onClick={() => (gpsPos ? setFilterMode("near") : requestGps())}
              testid="filter-near"
              icon={<NavigationArrow size={11} weight="bold" />}
            >
              {filterMode === "near" && gpsPos ? "Near me" : "Near me"}
            </FilterChip>
          </div>

          <div className="relative mb-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={courseQ}
                onChange={(e) => {
                  setCourseQ(e.target.value);
                  setShowSuggest(true);
                  setSelectedCourseId("");
                }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                placeholder={
                  filterMode === "favs"
                    ? "Search your favourites…"
                    : filterMode === "near"
                    ? "Search nearby courses…"
                    : "Search course or location…"
                }
                className="input-field flex-1"
                data-testid="create-league-course-search"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => addRequirement()}
                disabled={!selectedCourseId}
                className="btn-primary !py-3 !px-4"
                data-testid="create-league-add-course"
              >
                <Plus size={16} weight="bold" />
              </button>
            </div>
            {showSuggest && (
              <div
                className="absolute z-20 mt-1 left-0 right-12 max-h-60 overflow-y-auto rounded-xl border border-[var(--border)] bg-white shadow-lg"
                data-testid="create-league-course-suggestions"
              >
                {filteredCourses.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-[var(--text-secondary)] italic">
                    {filterMode === "favs"
                      ? "No favourites yet — star courses on the Courses page first."
                      : filterMode === "near" && !gpsPos
                      ? "Waiting for GPS…"
                      : `No matches for "${courseQ}"`}
                  </div>
                ) : (
                  filteredCourses.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => addRequirement(c.id)}
                      data-testid={`create-league-course-option-${c.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--bg-app)] flex items-start justify-between border-b border-[var(--border)] last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-bold text-[var(--primary)] text-sm line-clamp-1 flex items-center gap-1">
                          {favIds.has(c.id) && (
                            <Star size={11} weight="fill" className="text-amber-400 flex-shrink-0" />
                          )}
                          {c.name}
                        </div>
                        {c.location && (
                          <div className="text-[10px] text-[var(--text-secondary)] line-clamp-1">{c.location}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        {filterMode === "near" && c._dist != null && c._dist !== Infinity && (
                          <span className="text-[10px] font-bold text-[var(--text-secondary)] font-mono-num">
                            {c._dist < 1 ? "<1" : Math.round(c._dist)}mi
                          </span>
                        )}
                        <Plus size={14} weight="bold" className="text-[var(--primary)] mt-0.5" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {requirements.length === 0 ? (
            <div className="text-[11px] text-[var(--text-secondary)]">No courses — members can play anything in this league.</div>
          ) : (
            <div className="space-y-2" data-testid="create-league-requirements">
              {requirements.map((r, i) => (
                <div key={r.course_id} className="surface-card-flat p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-[var(--primary)] text-sm line-clamp-1">{r.course_name}</div>
                    <div className="text-[11px] text-[var(--text-secondary)]">play this course {r.rounds_required} {r.rounds_required === 1 ? "time" : "times"}</div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <button type="button" onClick={() => setRoundsForReq(i, -1)} className="h-8 w-8 rounded-full bg-[var(--secondary)] flex items-center justify-center" data-testid={`req-${i}-dec`}>
                      <Minus size={14} weight="bold" />
                    </button>
                    <span className="font-mono-num font-bold text-[var(--primary)] w-6 text-center" data-testid={`req-${i}-count`}>{r.rounds_required}</span>
                    <button type="button" onClick={() => setRoundsForReq(i, +1)} className="h-8 w-8 rounded-full bg-[var(--primary)] text-white flex items-center justify-center" data-testid={`req-${i}-inc`}>
                      <Plus size={14} weight="bold" />
                    </button>
                    <button type="button" onClick={() => removeRequirement(i)} className="h-8 w-8 rounded-full bg-[var(--accent-terracotta)] text-white flex items-center justify-center ml-1" data-testid={`req-${i}-remove`}>
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scoring rule */}
        <div>
          <label className="label-uppercase block mb-2">Scoring rule</label>
          <div className="space-y-2">
            {rules.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRuleType(r.id)}
                data-testid={`rule-${r.id}`}
                className={`w-full text-left rounded-2xl border-2 p-3 transition-colors ${
                  ruleType === r.id
                    ? "border-[var(--primary)] bg-[var(--accent-sand)]"
                    : "border-[var(--border-default)] bg-white"
                }`}
              >
                <div className="font-heading font-bold text-[var(--primary)]">{r.label}</div>
                <div className="text-xs text-[var(--text-secondary)]">{r.desc}</div>
              </button>
            ))}
          </div>
          {ruleType === "stableford" && (
            <label
              className="mt-2 flex items-center gap-2 rounded-2xl px-3 py-2 cursor-pointer"
              style={{ background: "var(--accent-sand)" }}
              data-testid="league-stableford-net-row"
            >
              <input
                type="checkbox"
                checked={stablefordNet}
                onChange={(e) => setStablefordNet(e.target.checked)}
                data-testid="league-stableford-net-toggle"
                className="h-4 w-4 accent-[var(--primary)]"
              />
              <span className="text-sm">
                <span className="font-heading font-bold text-[var(--primary)]">Net stableford</span>
                <span className="text-[11px] text-[var(--text-secondary)] block">Apply each player&apos;s handicap on every hole.</span>
              </span>
            </label>
          )}
        </div>
        {error && <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}
        <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="submit-create-league">
          {submitting ? "Creating..." : "Create league"}
        </button>
      </form>
    </Sheet>
  );
}

function JoinLeagueDialog({ onClose, onJoined }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/leagues/join", { code: code.toUpperCase() });
      onJoined();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet title="Join league" onClose={onClose} testid="join-league-dialog">
      <form onSubmit={submit} className="space-y-4 p-5">
        <div>
          <label className="label-uppercase block mb-2">League code</label>
          <input
            className="input-field uppercase tracking-[0.3em] text-center font-mono-num font-bold text-xl"
            value={code}
            onChange={(e)=>setCode(e.target.value.toUpperCase().slice(0,6))}
            maxLength={6}
            required
            placeholder="XXXXXX"
            data-testid="join-league-code"
          />
        </div>
        {error && <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}
        <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="submit-join-league">
          {submitting ? "Joining..." : "Join league"}
        </button>
      </form>
    </Sheet>
  );
}

function Sheet({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" data-testid={testid}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[var(--border-default)] px-5 py-4 flex items-center justify-between">
          <h2 className="font-heading font-bold text-xl text-[var(--primary)]">{title}</h2>
          <button onClick={onClose} className="btn-ghost" data-testid="close-sheet"><X size={20} weight="bold" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, testid, icon, count, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
        active
          ? "bg-[var(--primary)] text-white border border-[var(--primary)]"
          : "bg-transparent text-[var(--text-secondary)] border border-[var(--border)] hover:bg-[var(--bg-app)]"
      }`}
    >
      {icon}
      <span>{children}</span>
      {typeof count === "number" && count > 0 && (
        <span className={`text-[9px] ${active ? "opacity-80" : "opacity-60"}`}>· {count}</span>
      )}
    </button>
  );
}

